import React, { useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Button, Popup, Checkbox } from 'components/UIElements'
import STRINGS from 'utils/strings'

const SelectionRulesPanel = ({
  minChoices,
  maxChoices,
  totalOptions,
  required,
  minChoicesEnabled,
  maxChoicesEnabled,
  onUpdateMinChoices,
  onUpdateMaxChoices,
  onToggleMinChoicesEnabled,
  onToggleMaxChoicesEnabled,
}) => {
  const initializedRef = useRef(false)
  const prevTotalOptionsRef = useRef(totalOptions)
  const maxAutoRef = useRef(false)

  // Compute normalized values with defaults
  const normalizedMin = useMemo(() => {
    if (minChoices !== undefined && minChoices !== null) {
      return minChoices
    }
    return required ? 1 : 0
  }, [minChoices, required])

  const normalizedMax = useMemo(() => {
    if (maxChoices !== undefined && maxChoices !== null) {
      return maxChoices
    }
    return totalOptions || 0
  }, [maxChoices, totalOptions])

  // Calculate absolute minimum and maximum bounds
  const absoluteMin = required ? 1 : 0
  const absoluteMax = totalOptions || 0

  // When a rule is enabled and its value is still unset, materialize defaults into state.
  // This is important when min/max are disabled by default and later toggled on.
  useEffect(() => {
    if (totalOptions === 0) return

    if (minChoicesEnabled !== false && (minChoices === undefined || minChoices === null)) {
      onUpdateMinChoices(absoluteMin)
    }

    if (maxChoicesEnabled !== false && (maxChoices === undefined || maxChoices === null)) {
      onUpdateMaxChoices(absoluteMax)
      maxAutoRef.current = true
    }
  }, [
    totalOptions,
    minChoicesEnabled,
    minChoices,
    absoluteMin,
    maxChoicesEnabled,
    maxChoices,
    absoluteMax,
    onUpdateMinChoices,
    onUpdateMaxChoices,
  ])

  // Mark initialized once options exist (enables validation logic and option-count tracking).
  useEffect(() => {
    if (!initializedRef.current && totalOptions > 0) {
      initializedRef.current = true
      prevTotalOptionsRef.current = totalOptions
    }
  }, [totalOptions])

  // Validate and synchronize min/max values (only if enabled)
  useEffect(() => {
    if (!initializedRef.current || totalOptions === 0) return

    // Only validate if the respective toggle is enabled
    if (minChoicesEnabled !== false) {
      // Calculate valid bounds
      const minLowerBound = required ? 1 : 0
      const minUpperBound = absoluteMax

      // Clamp values to absolute bounds first
      let newMin = Math.max(minLowerBound, Math.min(normalizedMin, minUpperBound))

      // Ensure min <= max (synchronize) if max is also enabled
      if (maxChoicesEnabled !== false && newMin > normalizedMax) {
        newMin = normalizedMax
      }

      // Update value if it changed
      if (newMin !== normalizedMin) {
        onUpdateMinChoices(newMin)
      }
    }

    if (maxChoicesEnabled !== false) {
      // Calculate valid bounds
      const maxLowerBound = 1
      const maxUpperBound = absoluteMax

      // Clamp values to absolute bounds first
      let newMax = Math.max(maxLowerBound, Math.min(normalizedMax, maxUpperBound))

      // Ensure min <= max (synchronize) if min is also enabled
      if (minChoicesEnabled !== false && newMax < normalizedMin) {
        newMax = normalizedMin
      }

      // Update value if it changed
      if (newMax !== normalizedMax) {
        onUpdateMaxChoices(newMax)
      }
    }
  }, [
    normalizedMin,
    normalizedMax,
    required,
    absoluteMax,
    totalOptions,
    minChoicesEnabled,
    maxChoicesEnabled,
    onUpdateMinChoices,
    onUpdateMaxChoices,
  ])

  // Update min when required changes (only if enabled)
  useEffect(() => {
    if (!initializedRef.current || minChoicesEnabled === false) return

    if (required && normalizedMin < 1) {
      onUpdateMinChoices(1)
    } else if (!required && normalizedMin === 1 && minChoices === 1) {
      // Only auto-adjust if it was the default required value
      onUpdateMinChoices(0)
    }
  }, [required, minChoicesEnabled]) // Only react to required changes

  // Update max when totalOptions changes (only if enabled)
  useEffect(() => {
    if (!initializedRef.current || totalOptions === 0 || maxChoicesEnabled === false) return

    const prevTotalOptions = prevTotalOptionsRef.current
    const nextTotalOptions = absoluteMax

    // If options were removed, clamp Max down to the new absolute maximum.
    if (normalizedMax > nextTotalOptions) {
      onUpdateMaxChoices(nextTotalOptions)
    } else if (nextTotalOptions > prevTotalOptions) {
      // If options were added, auto-increase Max to match total options ONLY if Max was previously
      // following the default (i.e., it matched the previous option count) or was never manually overridden.
      const maxWasFollowingDefault =
        maxAutoRef.current || (maxChoices !== undefined && maxChoices !== null && maxChoices === prevTotalOptions)

      if (maxWasFollowingDefault) {
        onUpdateMaxChoices(nextTotalOptions)
        maxAutoRef.current = true
      }
    }

    prevTotalOptionsRef.current = nextTotalOptions
  }, [totalOptions, absoluteMax, normalizedMax, maxChoices, maxChoicesEnabled, onUpdateMaxChoices])

  const handleMinChange = delta => {
    const currentMin = normalizedMin
    const newMin = currentMin + delta

    // Calculate valid min value
    const minLowerBound = required ? 1 : 0
    const minUpperBound = maxChoicesEnabled !== false ? Math.min(normalizedMax, absoluteMax) : absoluteMax
    const validMin = Math.max(minLowerBound, Math.min(newMin, minUpperBound))

    onUpdateMinChoices(validMin)

    // If new min exceeds max, adjust max (only if max is enabled)
    if (maxChoicesEnabled !== false && validMin > normalizedMax) {
      onUpdateMaxChoices(validMin)
    }
  }

  const handleMaxChange = delta => {
    const currentMax = normalizedMax
    const newMax = currentMax + delta

    // Calculate valid max value
    const maxLowerBound = minChoicesEnabled !== false ? Math.max(normalizedMin, 1) : 1
    const maxUpperBound = absoluteMax
    const validMax = Math.max(maxLowerBound, Math.min(newMax, maxUpperBound))

    onUpdateMaxChoices(validMax)
    // Any manual adjustment stops auto-tracking the option count.
    maxAutoRef.current = false

    // If new max is less than min, adjust min (only if min is enabled)
    if (minChoicesEnabled !== false && validMax < normalizedMin) {
      onUpdateMinChoices(validMax)
    }
  }

  // Calculate button disabled states
  const minDecreaseDisabled = normalizedMin <= absoluteMin
  const minIncreaseDisabled = normalizedMin >= absoluteMax || normalizedMin >= normalizedMax

  const maxDecreaseDisabled = normalizedMax <= 1 || normalizedMax <= normalizedMin
  const maxIncreaseDisabled = normalizedMax >= absoluteMax

  if (totalOptions === 0) {
    return null
  }

  return (
    <div className='selection-rules attribute panel'>
      <h5 className='label-small med-font'>Selection Rules</h5>
      <div className='selection-rules-content'>
        <div className='selection-rule-item'>
          <div className='selection-rule-header'>
            <Checkbox
              checked={maxChoicesEnabled !== false}
              onClick={onToggleMaxChoicesEnabled}
              toggle
            />
            <span className='selection-rule-label'>Max selectable options:</span>
            <Popup position='right' trigger={<i className='fas fa-info-circle' />}>
              <p>{STRINGS.selectionRulesMaxTooltip}</p>
            </Popup>
          </div>
          {maxChoicesEnabled !== false && (
            <div className='selection-rule-controls'>
              <Button
                className='inline flexed'
                onClick={() => handleMaxChange(-1)}
                circle
                disabled={maxDecreaseDisabled}
                icon='fas fa-minus'
              />
              <p className='inline flexed'>{normalizedMax}</p>
              <Button
                className='inline flexed'
                onClick={() => handleMaxChange(1)}
                circle
                disabled={maxIncreaseDisabled}
                icon='fas fa-plus'
              />
            </div>
          )}
        </div>
        <div className='selection-rule-item'>
          <div className='selection-rule-header'>
            <Checkbox
              checked={minChoicesEnabled !== false}
              onClick={onToggleMinChoicesEnabled}
              toggle
            />
            <span className='selection-rule-label'>Min selectable options:</span>
            <Popup position='right' trigger={<i className='fas fa-info-circle' />}>
              <p>{STRINGS.selectionRulesMinTooltip}</p>
            </Popup>
          </div>
          {minChoicesEnabled !== false && (
            <div className='selection-rule-controls'>
              <Button
                className='inline flexed'
                onClick={() => handleMinChange(-1)}
                circle
                disabled={minDecreaseDisabled}
                icon='fas fa-minus'
              />
              <p className='inline flexed'>{normalizedMin}</p>
              <Button
                className='inline flexed'
                onClick={() => handleMinChange(1)}
                circle
                disabled={minIncreaseDisabled}
                icon='fas fa-plus'
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

SelectionRulesPanel.propTypes = {
  minChoices: PropTypes.number,
  maxChoices: PropTypes.number,
  totalOptions: PropTypes.number.isRequired,
  required: PropTypes.bool.isRequired,
  minChoicesEnabled: PropTypes.bool,
  maxChoicesEnabled: PropTypes.bool,
  onUpdateMinChoices: PropTypes.func.isRequired,
  onUpdateMaxChoices: PropTypes.func.isRequired,
  onToggleMinChoicesEnabled: PropTypes.func.isRequired,
  onToggleMaxChoicesEnabled: PropTypes.func.isRequired,
}

export default SelectionRulesPanel
